// ─────────────────────────────────────────────────────────────────────────
// RATED BILLING — per-plan credit pricing (NEW, isolated, feature-flagged).
//
// This is the single source of truth for the prototype's "per-plan rate"
// credit economy (panda_billing_states.html). It is deliberately SEPARATE from
// the live fixed-tier pricing in lib/plans.ts (Pro = 2000 cr / $79 flat). The
// two coexist; which one a user sees is gated by isRatedBillingEnabled().
//
// Pricing model (rates are CENTS per credit — may be fractional; the price
// math rounds the whole-purchase total, never the per-credit rate):
//   - planRateCents  : recurring subscription cost per monthly credit.
//   - topupRateCents : one-off / auto top-up cost per credit.
//   - baseCredits    : the plan's included monthly credits (the floor an
//                      "upgrade plan credits" total is measured against).
//
// PANDA-1414: every rated plan now shares ONE flat rate — $0.097/credit (9.7¢),
// 3× the old 3.3¢ base — for BOTH the subscription/upgrade rate and the top-up
// rate, so a credit costs the same however you buy it. The old per-plan ×2/×4
// multipliers are gone (all ×1); plans differ by the credits they INCLUDE
// (baseCredits: Pro 300, Grow 1,000, Scale 3,000), not by rate. Change
// RATED_RATE_CENTS to reprice every tier at once. (Legacy "starter" aliases Pro.)
// ─────────────────────────────────────────────────────────────────────────

import type { PaidPlanId } from "@/lib/plans";
import {
  tierMonthlyCents,
  tierAnnualCents,
  PLAN_BASE_CREDITS,
  type PaidTierId,
  type BillingCadence,
} from "@/lib/pricing/credit-tiers";

/** The single per-credit rate for every paid plan: $0.097 / credit (9.7¢).
 *  Mirrors BASE_RATE_CENTS_PER_CREDIT in lib/pricing/credit-tiers.ts — the
 *  subscription price is computed there; this drives the top-up price. */
export const RATED_RATE_CENTS = 9.7;

/** Per-plan price multiplier applied to the base rate. Flat ×1 on every plan
 *  (PANDA-1414) — kept as a symbol to mirror TIER_MULTIPLIER in
 *  lib/pricing/credit-tiers.ts. Never reintroduce ×2/×4 in only one of them. */
export const RATED_TIER_MULTIPLIER: Record<PaidPlanId, number> = {
  starter: 1,
  pro: 1,
  grow: 1,
  scale: 1,
};

// One-off top-up amounts offered in the rated "Buy credits" screen. These are
// purchase QUANTITIES (not a plan allowance), identical on every plan, priced
// per-credit at the flat rate above then rounded up to a whole dollar.
// The rungs are unchanged by PANDA-1414 — only the per-credit price moved — but
// the old rationale ("starts at 650 = the base credit tier") no longer holds,
// since no plan has a 650 base now. See defaultTopupAmount for how the default
// rung is chosen once 2× a small base falls at the bottom of this ladder.
export const RATED_TOPUP_LADDER = [
  650, 1500, 3000, 5000, 10000, 25000, 50000,
] as const;

export interface RatedPlan {
  /** Cents charged per monthly subscription credit (may be fractional). */
  planRateCents: number;
  /** Cents charged per one-off / auto top-up credit (may be fractional). */
  topupRateCents: number;
  /** Included monthly credits — the floor for "upgrade plan credits". */
  baseCredits: number;
  /** Selectable monthly-credit totals in the Upgrade dropdown (> base). */
  totals: number[];
  /** Selectable top-up amounts (Buy once + Auto top-up). */
  topups: number[];
  /** True once Product/Finance have signed off on the rates above. */
  ratesConfirmed: boolean;
}

// `totals` are the rungs ABOVE the plan's base — planTotalOptions() prepends
// baseCredits, reconstituting the full per-plan ladder from PLAN_CREDIT_LADDER
// (lib/pricing/credit-tiers.ts). A plan can never select below its own base.
export const RATED_PLANS: Record<PaidPlanId, RatedPlan> = {
  // Every plan shares the flat rate now (RATED_TIER_MULTIPLIER is ×1).
  // Legacy alias of Pro (not shown as a tab anymore).
  starter: {
    planRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.starter,
    topupRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.starter,
    baseCredits: PLAN_BASE_CREDITS.pro,
    totals: [600, 800, 1000, 1200, 2000, 3000, 4000, 5000, 7500, 10000],
    topups: [...RATED_TOPUP_LADDER],
    ratesConfirmed: true,
  },
  // Pro — 300 included credits, $29/mo.
  pro: {
    planRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.pro,
    topupRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.pro,
    baseCredits: PLAN_BASE_CREDITS.pro,
    totals: [600, 800, 1000, 1200, 2000, 3000, 4000, 5000, 7500, 10000],
    topups: [...RATED_TOPUP_LADDER],
    ratesConfirmed: true,
  },
  // Grow — 1,000 included credits, $97/mo.
  grow: {
    planRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.grow,
    topupRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.grow,
    baseCredits: PLAN_BASE_CREDITS.grow,
    totals: [1200, 2000, 3000, 4000, 5000, 7500, 10000],
    topups: [...RATED_TOPUP_LADDER],
    ratesConfirmed: true,
  },
  // Scale — 3,000 included credits, $291/mo.
  scale: {
    planRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.scale,
    topupRateCents: RATED_RATE_CENTS * RATED_TIER_MULTIPLIER.scale,
    baseCredits: PLAN_BASE_CREDITS.scale,
    totals: [4000, 5000, 7500, 10000],
    topups: [...RATED_TOPUP_LADDER],
    ratesConfirmed: true,
  },
};

// Trigger thresholds (credits) offered in the Auto top-up "balance falls below"
// dropdown, and the spend-limit multipliers (× top-up amount).
export const RATED_AUTO_TRIGGERS = [10, 25, 50, 100] as const;
export const RATED_AUTO_LIMIT_MULTS = [1, 2, 3, 4, 5, 10] as const;

export function isRatedPaidPlan(plan: string): plan is PaidPlanId {
  return (
    plan === "starter" ||
    plan === "pro" ||
    plan === "grow" ||
    plan === "scale"
  );
}
export function getRatedPlan(plan: string): RatedPlan | null {
  return isRatedPaidPlan(plan) ? RATED_PLANS[plan] : null;
}

// ── price math (all return whole cents) ────────────────────────────────────
//
// Subscription prices delegate to lib/pricing/credit-tiers.ts (the single
// source of truth for the pricing page) so the rated flow shows the SAME
// numbers: the dollar total is rounded to the NEAREST dollar at a flat 9.7¢/credit.
// e.g. Pro 300cr → $29, Grow 1,000cr → $97, Scale 3,000cr → $291; the SAME credit
// count costs the same on every plan. Annual = 2 months free (pay for 10). Legacy
// "starter" maps to Pro. Top-ups are per-credit at the same flat rate, rounded UP
// to a whole dollar (topupCents).

export type RatedCadence = BillingCadence;

/** Map a rated plan id onto a credit-tiers PaidTierId (starter ⇒ pro, ×1). */
function toTier(plan: PaidPlanId): PaidTierId {
  return plan === "starter" ? "pro" : plan;
}

/** Recurring monthly cost for a chosen total monthly credits. */
export function monthlyCents(plan: PaidPlanId, totalCredits: number): number {
  return tierMonthlyCents(toTier(plan), totalCredits);
}
/** Annual price (per YEAR) — 2 months free (pay for 10). */
export function annualCents(plan: PaidPlanId, totalCredits: number): number {
  return tierAnnualCents(toTier(plan), totalCredits);
}
/** Per-month figure shown on an annual card — the annual total ÷ 12, rounded
 *  to a WHOLE DOLLAR for display (e.g. $18, $37, $73). Informational only: the
 *  real charge is the exact annual total (annualCents). */
export function annualPerMonthCents(
  plan: PaidPlanId,
  totalCredits: number,
): number {
  return Math.round(annualCents(plan, totalCredits) / 12 / 100) * 100;
}
/** Price for the active cadence, as the card presents it (per-month cents). */
export function perMonthCents(
  plan: PaidPlanId,
  totalCredits: number,
  cadence: RatedCadence,
): number {
  return cadence === "annual"
    ? annualPerMonthCents(plan, totalCredits)
    : monthlyCents(plan, totalCredits);
}
/** Proration owed today when raising monthly credits from base → total. */
export function upgradeDueTodayCents(plan: PaidPlanId, totalCredits: number): number {
  return Math.max(
    0,
    monthlyCents(plan, totalCredits) -
      monthlyCents(plan, RATED_PLANS[plan].baseCredits),
  );
}
/** One-off / auto top-up cost for a credit amount, rounded UP to a whole
 *  dollar (top-ups are never a fractional-dollar charge). */
export function topupCents(plan: PaidPlanId, credits: number): number {
  return Math.ceil((credits * RATED_PLANS[plan].topupRateCents) / 100) * 100;
}

// ── validation (server trusts ONLY the credit amount; never a client price) ─

/** True if `total` is a valid Upgrade selection for the plan (> base). */
export function isValidUpgradeTotal(plan: PaidPlanId, total: number): boolean {
  return RATED_PLANS[plan].totals.includes(total);
}
/** Selectable monthly-credit totals for a NEW subscription (base + totals). */
export function planTotalOptions(plan: PaidPlanId): number[] {
  return [RATED_PLANS[plan].baseCredits, ...RATED_PLANS[plan].totals];
}
/** True if `total` is a valid new-subscription selection (base OR an upgrade total). */
export function isValidPlanTotal(plan: PaidPlanId, total: number): boolean {
  return planTotalOptions(plan).includes(total);
}
/**
 * Options for the "Upgrade plan credits" dropdown, seeded so the account's
 * CURRENT monthly allocation is always selectable (and therefore can be shown as
 * the initial selection). Grandfathered/arbitrary customs that aren't on the
 * ladder get injected + the list stays ascending & de-duped, so the modal never
 * has to fall back to the plan base just because the live total isn't a ladder
 * step. `null`/base current → the plain ladder.
 */
export function upgradeTotalOptions(
  plan: PaidPlanId,
  currentTotal?: number | null,
): number[] {
  const base = planTotalOptions(plan);
  if (
    typeof currentTotal === "number" &&
    Number.isFinite(currentTotal) &&
    currentTotal > 0 &&
    !base.includes(currentTotal)
  ) {
    return [...base, currentTotal].sort((a, b) => a - b);
  }
  return base;
}
/**
 * The credit total the Upgrade modal should OPEN on — the account's current
 * subscribed allocation, never a hardcoded default. Falls back to the plan base only
 * when there's no resolvable current total (fresh/base account). The result is
 * always a member of upgradeTotalOptions(plan, currentTotal).
 */
export function resolveUpgradeInitialTotal(
  plan: PaidPlanId,
  currentTotal?: number | null,
): number {
  if (
    typeof currentTotal === "number" &&
    Number.isFinite(currentTotal) &&
    currentTotal > 0
  ) {
    return Math.round(currentTotal);
  }
  return RATED_PLANS[plan].baseCredits;
}

/**
 * The monthly-credit rung the Upgrade ("Add more credits") modal should DEFAULT
 * to: the NEXT rung strictly ABOVE the account's current allocation. Per product,
 * a paid user is nudged UP a tier — the modal must never open on the tier they
 * already have (a no-op / redundant "upgrade"). Returns `null` when the account
 * is already at (or above) the highest rung, so the modal can show its
 * "highest tier" state instead of a pointless picker. When non-null, the result
 * is always a member of upgradeTotalOptions(plan, currentTotal) and strictly
 * greater than resolveUpgradeInitialTotal(plan, currentTotal).
 */
export function nextUpgradeTotal(
  plan: PaidPlanId,
  currentTotal?: number | null,
): number | null {
  const current = resolveUpgradeInitialTotal(plan, currentTotal);
  const options = upgradeTotalOptions(plan, currentTotal);
  const next = options.find((o) => o > current);
  return next ?? null;
}

/** The entry in `options` closest to `target` (ties resolve to the earlier). */
function nearestOption(options: readonly number[], target: number): number {
  return options.reduce(
    (best, o) => (Math.abs(o - target) < Math.abs(best - target) ? o : best),
    options[0],
  );
}

/**
 * The credit total a MONTHLY-plan picker should DEFAULT to when there is no
 * current allocation to preserve — per product, NEVER pre-select the cheapest
 * rung. We anchor at the ladder option NEAREST to 2× the plan's base (double the
 * lowest). With the PANDA-1414 bases this resolves to Pro 600, Grow 2,000, and
 * Scale 5,000 (nearest rung to 6,000). Always a member of planTotalOptions(plan).
 */
export function defaultPickerTotal(plan: PaidPlanId): number {
  return nearestOption(planTotalOptions(plan), RATED_PLANS[plan].baseCredits * 2);
}

/**
 * The credit total the Buy-a-plan modal (new subscription) should DEFAULT to:
 * the plan's cheapest selectable rung — its included base. Per product, the
 * plan modal opens on the LOWEST option so the entry price leads, whereas the
 * one-off top-up Buy screen keeps its 2×-base default (see defaultTopupAmount)
 * — which is why these two picker defaults are deliberately separate.
 * planTotalOptions is [base, ...higher], so [0] is always the smallest and is
 * guaranteed to be a valid option.
 */
export function lowestPlanTotal(plan: PaidPlanId): number {
  return planTotalOptions(plan)[0];
}

/**
 * The one-off TOP-UP amount the Buy screen should DEFAULT to — same "never the
 * cheapest rung" rule, applied to the top-up ladder instead of the monthly one.
 *
 * The anchor is 2× the plan's base, snapped to the nearest top-up rung. Since
 * PANDA-1414 shrank the bases (Pro 300), that anchor can land ON the cheapest
 * rung — 2×300 = 600 snaps to 650 — which is exactly what this rule exists to
 * avoid. So the cheapest rung is explicitly skipped: Pro 600 → 650 → 1,500,
 * Grow 2,000 → 1,500, Scale 6,000 → 5,000. Always a member of the plan's topups.
 */
export function defaultTopupAmount(plan: PaidPlanId): number {
  const topups = RATED_PLANS[plan].topups;
  const anchored = nearestOption(topups, RATED_PLANS[plan].baseCredits * 2);
  // Never pre-select the cheapest rung when a larger one exists.
  return anchored === topups[0] && topups.length > 1 ? topups[1] : anchored;
}
/** True if `credits` is a valid Buy-once / Auto top-up amount for the plan. */
export function isValidTopupAmount(plan: PaidPlanId, credits: number): boolean {
  return RATED_PLANS[plan].topups.includes(credits);
}
/** True if `trigger` is an offered Auto top-up balance threshold. */
export function isValidAutoTrigger(trigger: number): boolean {
  return (RATED_AUTO_TRIGGERS as readonly number[]).includes(trigger);
}
/** Valid monthly-cap (credits) options for a chosen top-up amount. */
export function autoLimitOptions(amount: number): number[] {
  return RATED_AUTO_LIMIT_MULTS.map((m) => amount * m);
}
export function isValidAutoLimit(amount: number, limitCredits: number): boolean {
  return autoLimitOptions(amount).includes(limitCredits);
}

// ── webhook grant validator (trust-but-verify the Stripe amount) ───────────
// The Stripe webhook MUST re-derive the expected charge from (plan, credits)
// and reject any mismatch, exactly like lib/plans.ts deriveArbitraryTopupGrant
// does for the flat-rate path — so a tampered/ stale session can never mint
// credits at the wrong price. Returns the grant to apply, or null to reject.
export function deriveRatedTopupGrant(
  plan: string,
  credits: number,
  amountCents: number,
): { credits: number; priceCents: number } | null {
  if (!isRatedPaidPlan(plan)) return null;
  if (!Number.isInteger(credits) || credits <= 0) return null;
  if (!isValidTopupAmount(plan, credits)) return null;
  const expected = topupCents(plan, credits);
  if (amountCents !== expected) return null;
  return { credits, priceCents: expected };
}

// ── feature flag ───────────────────────────────────────────────────────────
// Gates the entire rated-billing surface (route + endpoints). NEXT_PUBLIC_ so
// the client modals and the server routes read the same switch. Off by default.
export function isRatedBillingEnabled(): boolean {
  return process.env.NEXT_PUBLIC_RATED_BILLING_ENABLED === "1";
}
